<template>
  <article
    class="resume-card"
    :class="[
      `resume-card--${type}`,
      {
        'resume-card--deleting': isDeleting,
        'resume-card--last': isLast
      }
    ]"
    :aria-busy="isDeleting"
  >

    <!-- =====================================================
         TIMELINE
    ====================================================== -->

    <div class="timeline">

      <div class="timeline-node">

        <!-- CAREER -->
        <svg
          v-if="type === 'career'"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8 8V6.5C8 5.67 8.67 5 9.5 5H14.5C15.33 5 16 5.67 16 6.5V8"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <rect
            x="5"
            y="8"
            width="14"
            height="11"
            rx="2.5"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>


        <!-- EDUCATION -->
        <svg
          v-else-if="type === 'education'"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 9L12 5L20 9L12 13L4 9Z"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          />

          <path
            d="M7 11V15C9.7 17 14.3 17 17 15V11"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>


        <!-- LANGUAGE -->
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="7"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <path
            d="M5 12H19"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <path
            d="M12 5C14 7 15 9.2 15 12C15 14.8 14 17 12 19"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <path
            d="M12 5C10 7 9 9.2 9 12C9 14.8 10 17 12 19"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>

      </div>


      <div
        v-if="!isLast"
        class="timeline-line"
      ></div>

    </div>


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <div class="card-content">

      <div class="card-main">

        <!-- OPTIONAL LOGO -->

        <div
          v-if="logoUrl"
          class="logo-wrapper"
        >
          <img
            :src="logoUrl"
            alt=""
            class="logo"
          />
        </div>


        <div class="info">

          <div class="title">
            {{ title }}
          </div>


          <div
            v-if="subtitle"
            class="subtitle"
          >
            {{ subtitle }}
          </div>


          <div
            v-if="period"
            class="period"
          >

            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="6.5"
                stroke="currentColor"
                stroke-width="1.3"
              />

              <path
                d="M10 6.5V10L12.5 11.5"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <span>
              {{ period }}
            </span>

          </div>

        </div>

      </div>


      <!-- =====================================================
           DELETE
      ====================================================== -->

      <button
        class="delete-btn"
        type="button"
        :disabled="isDeleting"
        :aria-label="
          isDeleting
            ? 'Удаление'
            : 'Удалить'
        "
        @click="handleDelete"
      >

        <span
          v-if="isDeleting"
          class="delete-spinner"
        ></span>


        <svg
          v-else
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6.5 6.5L13.5 13.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />

          <path
            d="M13.5 6.5L6.5 13.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>

      </button>

    </div>


    <!-- =====================================================
         DELETE SHIMMER
    ====================================================== -->

    <div
      v-if="isDeleting"
      class="deleting-shimmer"
      aria-hidden="true"
    ></div>

  </article>
</template>


<script setup>

const props = defineProps({

  title: {
    type: String,
    default: ""
  },

  subtitle: {
    type: String,
    default: ""
  },

  period: {
    type: String,
    default: ""
  },

  logoUrl: {
    type: String,
    default: ""
  },

  type: {
    type: String,
    default: "career",
    validator: value =>
      [
        "career",
        "education",
        "language"
      ].includes(value)
  },

  isLast: {
    type: Boolean,
    default: false
  },

  isDeleting: {
    type: Boolean,
    default: false
  }

});


const emit = defineEmits([
  "delete"
]);


function handleDelete() {

  if (props.isDeleting) {
    return;
  }


  emit("delete");

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.resume-card {
  position: relative;

  width: 100%;

  display: grid;

  grid-template-columns:
    42px
    minmax(0, 1fr);

  box-sizing: border-box;

  overflow: hidden;

  font-family:
    "Eastman-Regular",
    sans-serif;

  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    filter 0.25s ease;
}


/* ============================================================
   TIMELINE
============================================================ */

.timeline {
  position: relative;

  display: flex;
  justify-content: center;
}


.timeline-node {
  position: relative;

  z-index: 2;

  width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 15px;

  color: #4e7ed5;

  background:
    #f3f7fc;

  border:
    1px solid
    rgba(
      50,
      105,
      195,
      0.10
    );

  border-radius: 10px;
}


.timeline-node svg {
  width: 16px;
  height: 16px;
}


/* Career */

.resume-card--career
.timeline-node {
  color: #4e7ed5;

  background:
    rgba(
      58,
      121,
      220,
      0.075
    );

  border-color:
    rgba(
      58,
      121,
      220,
      0.10
    );
}


/* Education */

.resume-card--education
.timeline-node {
  color: #725ed0;

  background:
    rgba(
      112,
      89,
      209,
      0.07
    );

  border-color:
    rgba(
      112,
      89,
      209,
      0.09
    );
}


/* Language */

.resume-card--language
.timeline-node {
  color: #349172;

  background:
    rgba(
      49,
      159,
      116,
      0.07
    );

  border-color:
    rgba(
      49,
      159,
      116,
      0.09
    );
}


.timeline-line {
  position: absolute;

  top: 47px;
  bottom: -15px;

  width: 1px;

  background:
    linear-gradient(
      to bottom,
      rgba(
        60,
        100,
        160,
        0.13
      ),
      rgba(
        60,
        100,
        160,
        0.04
      )
    );
}


/* ============================================================
   CONTENT
============================================================ */

.card-content {
  min-width: 0;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 14px;

  margin-bottom: 9px;

  padding:
    14px
    10px
    14px
    14px;

  background:
    rgba(
      248,
      249,
      251,
      0.74
    );

  border:
    1px solid
    rgba(
      25,
      40,
      60,
      0.06
    );

  border-radius: 14px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}


.resume-card:hover
.card-content {
  background: #ffffff;

  border-color:
    rgba(
      40,
      120,
      220,
      0.10
    );

  transform:
    translateY(-1px);

  box-shadow:
    0
    7px
    18px
    rgba(
      20,
      35,
      60,
      0.045
    );
}


.card-main {
  min-width: 0;

  display: flex;
  align-items: flex-start;

  gap: 11px;

  flex: 1;
}


/* ============================================================
   LOGO
============================================================ */

.logo-wrapper {
  width: 38px;
  height: 38px;

  flex:
    0
    0
    38px;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.06
    );

  border-radius: 11px;
}


.logo {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


/* ============================================================
   INFO
============================================================ */

.info {
  min-width: 0;

  flex: 1;
}


.title {
  overflow-wrap: anywhere;

  color: #343940;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  line-height: 1.35;
}


.subtitle {
  margin-top: 4px;

  overflow-wrap: anywhere;

  color: #8b9098;

  font-size: 9px;

  line-height: 1.4;
}


.period {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  margin-top: 7px;

  color: #477fd2;

  font-size: 8px;

  line-height: 1.3;
}


.period svg {
  width: 13px;
  height: 13px;

  flex:
    0
    0
    13px;
}


/* ============================================================
   DELETE
============================================================ */

.delete-btn {
  width: 29px;
  height: 29px;

  flex:
    0
    0
    29px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #a2a7af;

  background: transparent;

  border: none;
  border-radius: 9px;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}


.delete-btn svg {
  width: 15px;
  height: 15px;
}


.delete-btn:hover:not(:disabled) {
  color: #d85a5a;

  background:
    rgba(
      215,
      70,
      70,
      0.07
    );

  transform:
    scale(1.05);
}


.delete-btn:active:not(:disabled) {
  transform:
    scale(0.95);
}


.delete-btn:disabled {
  cursor: default;
}


/* ============================================================
   DELETING
============================================================ */

.resume-card--deleting {
  pointer-events: none;

  opacity: 0.62;

  transform:
    scale(0.995);

  filter:
    grayscale(0.1);
}


.delete-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      40,
      135,
      247,
      0.18
    );

  border-top-color:
    #2887f7;

  border-radius: 50%;

  animation:
    deleteSpin
    0.65s
    linear
    infinite;
}


.deleting-shimmer {
  position: absolute;

  inset: 0;

  z-index: 4;

  pointer-events: none;

  background:
    linear-gradient(
      110deg,
      transparent 20%,
      rgba(
        255,
        255,
        255,
        0.04
      ) 35%,
      rgba(
        255,
        255,
        255,
        0.5
      ) 50%,
      rgba(
        255,
        255,
        255,
        0.04
      ) 65%,
      transparent 80%
    );

  transform:
    translateX(-120%);

  animation:
    deletingShimmer
    1s
    ease-in-out
    infinite;
}


@keyframes deleteSpin {

  to {
    transform:
      rotate(360deg);
  }

}


@keyframes deletingShimmer {

  from {
    transform:
      translateX(-120%);
  }

  to {
    transform:
      translateX(120%);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.timeline-node {
  background:
    rgba(
      75,
      125,
      210,
      0.10
    );

  border-color:
    rgba(
      90,
      145,
      230,
      0.11
    );
}


html[data-theme="dark"]
.resume-card--education
.timeline-node {
  background:
    rgba(
      112,
      89,
      209,
      0.10
    );

  border-color:
    rgba(
      125,
      108,
      215,
      0.12
    );
}


html[data-theme="dark"]
.resume-card--language
.timeline-node {
  background:
    rgba(
      49,
      159,
      116,
      0.10
    );

  border-color:
    rgba(
      70,
      175,
      135,
      0.12
    );
}


html[data-theme="dark"]
.card-content {
  background:
    rgba(
      255,
      255,
      255,
      0.03
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.resume-card:hover
.card-content {
  background:
    rgba(
      255,
      255,
      255,
      0.045
    );

  border-color:
    rgba(
      100,
      155,
      240,
      0.10
    );

  box-shadow:
    0
    8px
    20px
    rgba(
      0,
      0,
      0,
      0.12
    );
}


html[data-theme="dark"]
.title {
  color: #e5e7ea;
}


html[data-theme="dark"]
.subtitle {
  color: #898e97;
}


html[data-theme="dark"]
.period {
  color: #7eaeef;
}


html[data-theme="dark"]
.logo-wrapper {
  background: #2b2d32;

  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.timeline-line {
  background:
    linear-gradient(
      to bottom,
      rgba(
        130,
        165,
        220,
        0.12
      ),
      rgba(
        130,
        165,
        220,
        0.025
      )
    );
}


html[data-theme="dark"]
.delete-btn {
  color: #7f848d;
}


html[data-theme="dark"]
.delete-btn:hover:not(:disabled) {
  color: #ef8e8e;

  background:
    rgba(
      230,
      90,
      90,
      0.08
    );
}


html[data-theme="dark"]
.deleting-shimmer {
  background:
    linear-gradient(
      110deg,
      transparent 20%,
      rgba(
        255,
        255,
        255,
        0.01
      ) 35%,
      rgba(
        255,
        255,
        255,
        0.08
      ) 50%,
      rgba(
        255,
        255,
        255,
        0.01
      ) 65%,
      transparent 80%
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 640px) {

  .resume-card {
    grid-template-columns:
      35px
      minmax(0, 1fr);
  }


  .timeline-node {
    width: 28px;
    height: 28px;

    margin-top: 14px;

    border-radius: 9px;
  }


  .timeline-node svg {
    width: 14px;
    height: 14px;
  }


  .timeline-line {
    top: 42px;
  }


  .card-content {
    padding:
      13px
      8px
      13px
      12px;
  }


  .logo-wrapper {
    width: 34px;
    height: 34px;

    flex-basis: 34px;

    border-radius: 10px;
  }


  .delete-btn {
    width: 27px;
    height: 27px;

    flex-basis: 27px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .resume-card,
  .card-content,
  .delete-btn {
    transition: none;
  }


  .delete-spinner,
  .deleting-shimmer {
    animation: none;
  }

}

</style>